import { useState } from 'react'
import { Modal } from 'Components/Modal'
import { Box, Grid } from '@mui/material'
import { sendGet, sendPost } from 'hooks/use-fetch'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'
import { TextAreaWithLabel } from 'Components/TextArea'
import { toast } from 'react-toastify'
import { SwitchWithLabel } from 'Components/Switch/SwitchWithLabel'
import { TextFieldWithLabel } from 'Components/TextField'

export interface ServiceDetailProps {
  open: boolean
  onClose: () => void
  onSubmit: (description: string, isIntake: boolean, excludeFromPortal: boolean, caregiverShortForm: boolean, minAge: number | null, maxAge: number | null) => void
  isdServiceID: number
  isdID: number
  description: string
  isIntake: boolean
  excludeFromPortal: boolean
  caregiverShortForm: boolean
  minAge: number | null
  maxAge: number | null
}

export interface DocumentSettings {
  id: number
  isdServiceID: number
  documentID: number
  status: string
}

export function ServiceDetail (props: ServiceDetailProps): JSX.Element {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [description, setDescription] = useState(props.description)
  const [isIntake, setIsIntake] = useState(props.isIntake)
  const [updateDisabled, setUpdateDisabled] = useState(false)
  const [excludeFromPortal, setExcludeFromPortal] = useState(props.excludeFromPortal)
  const [caregiverShortForm, setCaregiverShortForm] = useState(props.caregiverShortForm)
  const [minAge, setMinAge] = useState(props.minAge)
  const [maxAge, setMaxAge] = useState(props.maxAge)

  const handleIntakeChange = async (checked: boolean): Promise<void> => {
    if (checked) {
      const { response } = await sendGet(`/ISDServices/IsIntakeServiceInUse?isdServiceID=${props.isdServiceID}`, {})
      if (response === true) {
        toast.error('Only intake providers can use this service and currently non-intake providers are assigned this service. Please remove the service from them prior to making this change.')
        setUpdateDisabled(true)
      } else {
        toast.warning('Only intake providers will be able to utilize this service.')
        setUpdateDisabled(false)
      }
    } else {
      toast.warning('This service will now be available to any provider.')
      setUpdateDisabled(false)
    }

    setIsIntake(checked)
  }

  const submit = async (): Promise<void> => {
    const { response } = await sendPost('/ISDServices/UpdateService', { iSDServiceID: props.isdServiceID, description, isIntake, excludeFromPortal, caregiverShortForm, minAge, maxAge })
    if (response.success !== true) {
      toast.error(response.error[0])
      return
    }
    props.onSubmit(description, isIntake, excludeFromPortal, caregiverShortForm, minAge, maxAge)
  }

  const handleSubmit = async (): Promise<void> => {
    await submit()
  }

  const handleModalClose = (): void => {
    setShowConfirmationModal(false)
  }

  const content = <Box>
    <SwitchWithLabel
      onChange={async (e, checked) => { await handleIntakeChange(checked) }}
      label='Intake Service'
      name='isIntake'
      checked={isIntake}
      dataTestId='isIntake'
    />

    <SwitchWithLabel
      onChange={(e, checked) => { setExcludeFromPortal(checked) }}
      label='Exclude from Caregiver Portal'
      name='excludeFromPortal'
      checked={excludeFromPortal}
      dataTestId='excludeFromPortal'
    />

    <SwitchWithLabel
      onChange={(e, checked) => { setCaregiverShortForm(checked) }}
      label='Allow Caregiver Short Form'
      name='caregiverShortForm'
      checked={caregiverShortForm}
      dataTestId='caregiverShortForm'
    />

    <Grid container spacing={2}>
      <Grid item xs={6}>
        <TextFieldWithLabel
          name='minAge'
          label='Min. Age'
          dataTestId='minAge'
          value={minAge}
          onChange={(e) => { setMinAge(e.target.value) }}
        />
      </Grid>
      <Grid item xs={6}>
        <TextFieldWithLabel
          name='maxAge'
          label='Max. Age'
          dataTestId='maxAge'
          value={maxAge}
          onChange={(e) => { setMaxAge(e.target.value) }}
        />
      </Grid>
    </Grid>

    <TextAreaWithLabel
      name="description"
      label="Description"
      dataTestId='description'
      value={description}
      onChange={(e) => { setDescription(e.target.value) }}
    />

    {showConfirmationModal && <Dialog
      open={true}
      onClose={handleModalClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        {'Confirmation'}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          Changing these values will impact any in-progress enrollments. Do you wish to continue?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleModalClose}>Cancel</Button>
        <Button onClick={submit} data-testid='dialogOK' autoFocus>
          Ok
        </Button>
      </DialogActions>
    </Dialog>}
  </Box>

  return <Modal
    onClose={props.onClose}
    open={props.open}
    onConfirm={handleSubmit}
    title="Service Details"
    confirmationContent={content}
    isUpdateDisabled={updateDisabled}
  />
}
